/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { createRef, useEffect, useState } from 'react';
import { DATE, formatDate } from '@utils/date';
import { Button } from 'reactstrap';
import { getFullName } from '@utils/user';
import { naturalSort } from '@utils/index';
import { UserMessageModal } from '@features/message-modal/MessageModal';
import { SerializedError } from '@reduxjs/toolkit';
import { useHistory } from 'react-router-dom';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { useAppSelector } from '@reducers/index';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table, ColumnDef } from '@g17eco/molecules/table';
import { SortingFn } from '@tanstack/react-table';
import { DISABLED_DELEGATION_MESSAGE } from '@constants/user';
import { getRootConfig } from '@selectors/globalData';
import { UserWithDelegation, DelegationStats } from '@g17eco/types/initiativeStats';
import { UserProgressRow } from '../components/ProgressRow';

type UserRow = UserWithDelegation;
interface AdminUserTableParams {
  initiativeId: string;
  highlightUserId?: string | null;
  onUserClick?: (user: UserRow) => void;
  users: UserWithDelegation[];
  error?:
    | {
        message: string;
        name: string;
      }
    | SerializedError;
  toggleShowBulkUsersMessage: () => void;
}

const PROGRESS_COL_WIDTH = 200;

const createPercentageSortFn =
  (numeratorKey: keyof DelegationStats, denominatorKey: keyof DelegationStats): SortingFn<UserRow> =>
  (a, b) => {
    const aNumerator = a.original.delegationStats?.[numeratorKey] ?? 0;
    const aDenominator = a.original.delegationStats?.[denominatorKey] ?? 0;
    const bNumerator = b.original.delegationStats?.[numeratorKey] ?? 0;
    const bDenominator = b.original.delegationStats?.[denominatorKey] ?? 0;
    const aPercent = aDenominator !== 0 ? aNumerator / aDenominator : 0;
    const bPercent = bDenominator !== 0 ? bNumerator / bDenominator : 0;
    return aPercent - bPercent;
  };

const highlightClass = 'row-highlight-blink';
const removeClass: React.MouseEventHandler<HTMLDivElement> = (e) => e.currentTarget.classList.remove(highlightClass);

export const AdminUserTable = (props: AdminUserTableParams) => {
  const { initiativeId, onUserClick, users, error, highlightUserId, toggleShowBulkUsersMessage } = props;
  const [showUserMessage, setShowUserMessage] = useState<string | undefined>(undefined);
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessBulkDelegation = FeaturePermissions.canAccessBulkDelegation(rootConfig);
  const [lastScrollUserId, setLastScrollUserId] = useState<string>('');
  const history = useHistory();

  const userRowRef = createRef<HTMLDivElement>();
  useEffect(() => {
    if (userRowRef.current && highlightUserId && lastScrollUserId !== highlightUserId) {
      userRowRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
      });
      // Ensure we do not do it forever
      setLastScrollUserId(highlightUserId);
    }
  }, [userRowRef, highlightUserId, lastScrollUserId]);

  if (error && error.message) {
    return <BasicAlert type={'danger'}>{error.message}</BasicAlert>;
  }

  const columns: ColumnDef<UserRow>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      sortingFn: (a, b) => {
        return naturalSort(getFullName(a.original), getFullName(b.original));
      },
      cell: ({ row }) => {
        const data = row.original;
        const isHighlighted = data._id === highlightUserId;
        const tooltip = (
          <div key={`${data._id}`}>
            <strong>{getFullName(data)}</strong>
            <div>{data.email}</div>
          </div>
        );

        return (
          <div
            className={isHighlighted ? highlightClass : ''}
            onMouseEnter={removeClass}
            ref={isHighlighted ? userRowRef : undefined}
          >
            {onUserClick ? (
              <div className='d-flex'>
                <SimpleTooltip text={tooltip}>
                  <Button
                    color={'link'}
                    onClick={() => onUserClick(data)}
                    className={'text-truncate'}
                    style={{ display: 'block' }}
                  >
                    {getFullName(data)}
                  </Button>
                </SimpleTooltip>
                {data.isStaff ? (
                  <SimpleTooltip text='Staff user. This user is not visible to non-staff.'>
                    <i className='fa fa-user-ninja text-ThemeDangerMedium' />
                  </SimpleTooltip>
                ) : null}
              </div>
            ) : (
              <span className={'text-truncate'} style={{ display: 'block' }}>
                {getFullName(data)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Last Online',
      accessorFn: (row) => formatDate(row.lastLogin ?? '', DATE.MONTH_YEAR_SHORT),
      sortingFn: (a, b) => naturalSort(a.original.lastLogin ?? '', b.original.lastLogin ?? ''),
    },
    {
      header: 'Submitted',
      meta: {
        cellProps: {
          style: {
            width: PROGRESS_COL_WIDTH,
          },
        },
      },
      cell: ({ row }) => {
        const { updated = 0, contributor = 0 } = row.original.delegationStats || {};
        return (
          <UserProgressRow
            numerator={updated}
            denominator={contributor}
            className={'background-ThemeAccentMedium'}
          />
        );
      },
      sortingFn: createPercentageSortFn('updated', 'contributor'),
    },
    {
      header: 'Verified',
      meta: {
        cellProps: {
          style: {
            width: PROGRESS_COL_WIDTH,
          },
        },
      },
      cell: ({ row }) => {
        const { verified = 0, verifier = 0 } = row.original.delegationStats || {};
        return (
          <UserProgressRow
            numerator={verified}
            denominator={verifier}
            className={'background-ThemeSuccessMedium'}
          />
        );
      },
      sortingFn: createPercentageSortFn('verified', 'verifier'),
    },
    {
      id: 'actions',
      header: () => (
        <Button onClick={toggleShowBulkUsersMessage} color={'link'} className='btn-xs'>
          <i className='fa-light fa-envelopes me-1' />
          Message all
        </Button>
      ),
      meta: {
        headerProps: {
          style: { textAlign: 'center' },
        },
        cellProps: {
          style: {
            textAlign: 'center',
            width: 200,
          },
        },
      },
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className='d-flex gap-2 justify-content-center'>
            {canAccessBulkDelegation && (
              <SimpleTooltip text={data.delegateDisabled ? DISABLED_DELEGATION_MESSAGE : 'Delegate to user'}>
                <Button
                  size={'sm'}
                  color={'secondary'}
                  disabled={data.delegateDisabled}
                  className={'btn-xs'}
                  onClick={() =>
                    history.push(generateUrl(ROUTES.SUBSIDIARY_USER_DELEGATION, { initiativeId, userId: data._id }))
                  }
                >
                  <i className='fa-light fa-user-gear me-1' />
                  Delegate
                </Button>
              </SimpleTooltip>
            )}
            <SimpleTooltip text='Message user'>
              <Button size={'sm'} color={'secondary'} className={'btn-xs'} onClick={() => setShowUserMessage(data._id)}>
                <i className='fa-light fa-envelope me-1' />
                Message
              </Button>
            </SimpleTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Table
        responsive
        pageSize={10}
        columns={columns}
        data={users}
        sortBy={[{ id: 'name', desc: false }]}
        noData={<>No results</>}
      />
      {showUserMessage ? (
        <UserMessageModal users={users} userIds={[showUserMessage]} toggle={() => setShowUserMessage(undefined)} />
      ) : null}
    </>
  );
};
